// Warehouse orders (PX-WHS-120) — data access. A read-only overview derived
// from the origin documents (docs/design/warehouse-orders.md):
//
//   warehouse_order_page(company, scope, direction, term, warehouse, after, limit)
//     one statement; keyset on (planned date, kind, id); limit + 1 = has more
//   warehouse_order_lines(kind, id)
//     the order's lines, one statement per origin
//
// Lists: open scope reads each origin's open partial index; ~30 rows × 10
// columns per page (≈ 8 KB). Detail: one round-trip, prefetched on hover.

import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
// Types only: this module is on the route loader's path (entry chunk), so
// it must not pull zod in; the Zod inputs in @patronix/domain are the agent
// tools' contract and the server validates every parameter again.
import type {
  WarehouseOrderCursor,
  WarehouseOrderDirection,
  WarehouseOrderKind,
  WarehouseOrderLine,
  WarehouseOrderRow,
  WarehouseOrderScope,
} from "@patronix/domain/warehouse-orders";
import type { Page } from "@/components/DataList";
import { isDemoMode, supabase } from "@/lib/supabase";

export const WO_PAGE = 100;
const STALE = 30_000;

export interface WarehouseOrderListParams {
  companyId: string;
  scope: WarehouseOrderScope;
  direction: WarehouseOrderDirection | null;
  q: string;
  warehouseId: string | null;
}

function db() {
  if (!supabase) throw new Error("Supabase is not configured");
  return supabase;
}

export const warehouseOrderKeys = {
  all: ["warehousing", "warehouse-orders"] as const,
  list: (p: WarehouseOrderListParams) => [...warehouseOrderKeys.all, "list", p] as const,
  lines: (kind: WarehouseOrderKind, id: string) => [...warehouseOrderKeys.all, "lines", kind, id] as const,
};

export async function listWarehouseOrders(
  p: WarehouseOrderListParams,
  cursor: string | null,
  signal: AbortSignal,
  size = WO_PAGE,
): Promise<Page<WarehouseOrderRow>> {
  if (isDemoMode) return { rows: [], nextCursor: null };
  const input = {
    p_company_id: p.companyId,
    p_scope: p.scope,
    p_direction: p.direction,
    p_term: p.q.trim() || null,
    p_warehouse_id: p.warehouseId,
    p_after: cursor ? (JSON.parse(cursor) as WarehouseOrderCursor) : null,
    p_limit: size + 1,
  };
  const { data, error } = await db().rpc("warehouse_order_page", input).abortSignal(signal);
  if (error) throw error;
  const rows = (data ?? []) as WarehouseOrderRow[];
  const hasMore = rows.length > size;
  const page = hasMore ? rows.slice(0, size) : rows;
  const last = page[page.length - 1];
  return { rows: page, nextCursor: hasMore && last ? JSON.stringify(last.cursor) : null };
}

export function warehouseOrderListQuery(p: WarehouseOrderListParams) {
  return infiniteQueryOptions({
    queryKey: warehouseOrderKeys.list(p),
    queryFn: ({ pageParam, signal }) => listWarehouseOrders(p, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last: Page<WarehouseOrderRow>) => last.nextCursor,
    staleTime: STALE,
    enabled: !!p.companyId,
  });
}

export function warehouseOrderLinesQuery(kind: WarehouseOrderKind, id: string) {
  return queryOptions({
    queryKey: warehouseOrderKeys.lines(kind, id),
    queryFn: async ({ signal }) => {
      if (isDemoMode) return [] as WarehouseOrderLine[];
      const input = { p_kind: kind, p_id: id };
      const { data, error } = await db().rpc("warehouse_order_lines", input).abortSignal(signal);
      if (error) throw error;
      return (data ?? []) as WarehouseOrderLine[];
    },
    staleTime: STALE,
  });
}

/** The origin document's own screen (where it is posted). */
export function sourceLink(kind: WarehouseOrderKind, id: string): { to: string; search: Record<string, string> } {
  switch (kind) {
    case "purchase_receipt":
      return { to: "/procurement/purchase-orders", search: { status: "all", po: id } };
    case "sales_shipment":
      return { to: "/sales/orders", search: { status: "all", so: id } };
    case "production_issue":
    case "production_receipt":
      return { to: "/manufacturing/production-orders", search: { status: "all", po: id } };
    case "transfer":
      return { to: "/warehousing/transfers", search: { status: "all", doc: id } };
    case "inspection":
      return { to: "/quality/inspection-orders", search: { status: "all", id } };
  }
}
